import { eAtlas, eUIAtlasFrame, Game } from "../../Game";
import { Pool } from "../../Utils/Collections/Pool";
import { WrappedArray } from "../../Utils/Collections/WrapperArray";
import { AtlasUtils } from "../../Utils/Atlas/AtlasUtils";
import { HUD } from "./HUD";

/**
 * Zobrazeni swipu
 */
export class SwipeDisplay {
    private static readonly DOT_SIZE = 12;
    private static readonly DOT_SPACING = 24;

    /**
     * Template pro vytvoreni entity s teckou
     */
    private static _dotEntTemplate: pc.Entity;
    /**
     * Pool s volnymi entitami tecek (sdileny mezi instancemi)
     */
    private static _freeDots: Pool<pc.Entity>;
    /**
     * Seznam aktivnich entit tecek
     */
    private _actDots: WrappedArray<pc.Entity>;
    /**
     * Opacity zobrazit (0 az 1)
     */
    get opacity(): number { return this._opacity; }
    set opacity(opacity: number) {
        if (opacity > 1) {
            opacity = 1;
        }
        else if (opacity < 0) {
            opacity = 0;
        }

        if (opacity != this._opacity) {
            this._opacity = opacity;

            this._actDots.forEach((dot: pc.Entity): void => {
                dot.element.opacity = opacity;
            });
        }
    }
    private _opacity: number;
    /**
     * Pozice posledni zobrazene tecky
     */
    private _lastDotPos = new pc.Vec2();
    /**
     * Delka swipu pri zobrazeni posledni tecky
     */
    private _lastDotSwipeLen: number;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor() {
        // Pool s volnymi entitami tecek
        if (!SwipeDisplay._freeDots) {
            let atlas = Game.getAtlas(eAtlas.ui);
            let batchGroupId = Game.app.batcher.addGroup("swipe", true, 100, undefined, [Game.app.scene.layers.getLayerByName("UI").id]).id;

            SwipeDisplay._freeDots = new Pool<pc.Entity>(undefined, 10, true, (): pc.Entity => {
                let e: pc.Entity;

                if (!SwipeDisplay._dotEntTemplate) {
                    let texture = atlas.texture;
                    let frameRc = (<pc.TextureAtlasFrame>atlas.frames[eUIAtlasFrame.aimDot]).rect;
                    let rc = AtlasUtils.getTexFrameRegion(texture, frameRc);

                    SwipeDisplay._dotEntTemplate = e = new pc.Entity();
                    e.addComponent(pc.eCompomentType.element, {
                        type: pc.ELEMENTTYPE_IMAGE,
                        texture: texture,
                        anchor: [0, 1, 0, 1],
                        pivot: [0.5, 0.5],
                        rect: rc,
                        width: SwipeDisplay.DOT_SIZE,
                        height: SwipeDisplay.DOT_SIZE,
                        batchGroupId: batchGroupId,
                        opacity: 0.5
                    });
                }
                else {
                    e = SwipeDisplay._dotEntTemplate.clone() as pc.Entity;
                }

                e.enabled = false;
                HUD.instance.screen.addChild(e);

                return e;
            });
        }

        // Seznam aktivnich entit tecek
        this._actDots = new WrappedArray<pc.Entity>();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    reset(): void {
        // Uvolnit aktivni tecky
        if (!this._actDots.empty) {
            this._actDots.forEach((dot: pc.Entity): void => {
                dot.enabled = false;
                SwipeDisplay._freeDots.returnItem(dot);
            });

            this._actDots.clear(true);
        }

        this._opacity = 0;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    addPoint(x: number, y: number, swipeLen: number): void {
        if (swipeLen == 0) {
            // Prvni bod swipu
            this._lastDotSwipeLen = 0;
            this._lastDotPos.set(x, y);
            this.showDot(0, 0);
        }
        else {
            let spacing = SwipeDisplay.DOT_SPACING;

            if (swipeLen - spacing >= this._lastDotSwipeLen) {
                // Smer z pozice posledni tecky do aktualni pozice
                let dir = Math.atan2(y - this._lastDotPos.y, x - this._lastDotPos.x);

                while (swipeLen - spacing >= this._lastDotSwipeLen) {
                    this.showDot(spacing, dir);
                    this._lastDotSwipeLen += spacing;
                }
            }
        }
    }

    //-------------------------------------------------------------------------
    /**
     * Zobrazeni nove tecky
     * @param dis Vzdalenost od pozice posledni tecky
     * @param dir Smer v radianech od pozice posledni tecky
     */
    //-------------------------------------------------------------------------
    private showDot(dis: number, dir: number): void {
        let x = this._lastDotPos.x + Math.cos(dir) * dis;
        let y = this._lastDotPos.y + Math.sin(dir) * dis;
        let dot = SwipeDisplay._freeDots.getItem();

        this._lastDotPos.set(x, y);

        dot.setLocalPosition(x, y, 0);
        dot.element.opacity = this._opacity;
        dot.enabled = true;
        this._actDots.add(dot);
    }
}